import { createContext, useCallback, useContext, useEffect, useState } from "react";

const CACHE_PREFIX = "palzea_translations_";
const DEFAULT_LOCALE = "en";
const LOCALE_SHAPE = /^[a-z]{2}(-[a-z]{2})?$/;

// The widget has no meaningful URL-path locale segment of its own (it's
// embedded on a third-party page, or reached via a direct iframe src like
// SsoPage.jsx/Index.jsx's live demo in the merchant repo) - locale is
// resolved from whatever the embedder provides instead, same idea as an
// i18n library reading `lang` from init options. Priority:
//   1. ?locale= query param - what merchant/partner portal embeds pass so
//      "p2p-stage.palzea.com/en" embeds "widget-p2p-stage.palzea.com" showing
//      English too (and /es-es shows es-es), and what a partner's own site
//      can set explicitly.
//   2. A previously chosen locale for this browser (localStorage) - so a
//      returning visitor's manual language pick sticks across sessions.
//   3. navigator.language, matched down to its base language if the exact
//      region variant isn't available (e.g. "es-PE" -> "es" unless "es-pe"
//      is itself an active language).
//   4. "en".
// Like the merchant repo, no hardcoded list of valid codes - an
// unrecognized code just resolves to an empty translations object, and
// every t() call safely falls back to its own default string.
export function resolveInitialLocale() {
    try {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get("locale")?.toLowerCase();
        if (fromQuery && LOCALE_SHAPE.test(fromQuery)) return fromQuery;
    } catch { /* ignore */ }

    try {
        const stored = localStorage.getItem("palzea_locale");
        if (stored && LOCALE_SHAPE.test(stored)) return stored;
    } catch { /* ignore */ }

    try {
        const nav = (navigator.language || "").toLowerCase();
        if (LOCALE_SHAPE.test(nav)) return nav;
        const base = nav.split("-")[0];
        if (/^[a-z]{2}$/.test(base)) return base;
    } catch { /* ignore */ }

    return DEFAULT_LOCALE;
}

const LocaleContext = createContext({
    locale: DEFAULT_LOCALE,
    setLocale: () => { },
    t: (key, fallback) => fallback ?? key,
});

async function fetchTranslations(locale) {
    const cacheKey = `${CACHE_PREFIX}${locale}`;
    try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }

    try {
        const base = import.meta.env.VITE_API_BASE_URL;
        const res = await fetch(`${base}/public/lang/${locale}.json`);
        if (!res.ok) return {};
        const data = await res.json();
        try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch { /* ignore */ }
        return data;
    } catch {
        return {};
    }
}

export function LocaleProvider({ children }) {
    const [locale, setLocaleState] = useState(resolveInitialLocale);
    const [translations, setTranslations] = useState({});

    useEffect(() => {
        let cancelled = false;
        fetchTranslations(locale).then((data) => {
            if (!cancelled) setTranslations(data);
        });
        return () => { cancelled = true; };
    }, [locale]);

    const setLocale = useCallback((code) => {
        try { localStorage.setItem("palzea_locale", code); } catch { /* ignore */ }
        setLocaleState(code);
    }, []);

    const t = useCallback((key, fallback) => translations[key] ?? fallback ?? key, [translations]);

    return (
        <LocaleContext.Provider value={{ locale, setLocale, t }}>
            {children}
        </LocaleContext.Provider>
    );
}

export const useTranslation = () => useContext(LocaleContext);
